import React, { useState } from 'react';
import { Filter } from 'lucide-react';

// Example categories data (replace/expand as needed)
const CATEGORIES = [
  {
    label: 'Income',
    children: [
      { value: 'paychecks', label: '💵 Paychecks' },
      { value: 'interest', label: '💸 Interest' },
      { value: 'business_income', label: '🔥 Business Income' },
      { value: 'other_income', label: '🪙 Other Income' }
    ]
  },
  {
    label: 'Gifts & Donations',
    children: [
      { value: 'charity', label: '🎗️ Charity' },
      { value: 'gifts', label: '🎁 Gifts' }
    ]
  },
  {
    label: 'Auto & Transport',
    children: [
      { value: 'auto_payment', label: '🚗 Auto Payment' },
      { value: 'public_transit', label: '🚋 Public Transit' },
      { value: 'gas', label: '⛽ Gas' },
      { value: 'auto_maintenance', label: '🛠️ Auto Maintenance' },
      { value: 'parking_tolls', label: '🏢 Parking & Tolls' },
      { value: 'taxi_ride_shares', label: '🚖 Taxi & Ride Shares' }
    ]
  },
  {
    label: 'Housing',
    children: [
      { value: 'mortgage', label: '🏡 Mortgage' },
      { value: 'rent', label: '🏠 Rent' },
      { value: 'home_improvement', label: '🔨 Home Improvement' }
    ]
  },
  {
    label: 'Bills & Utilities',
    children: [
      { value: 'garbage', label: '🗑️ Garbage' },
      { value: 'water', label: '💧 Water' },
      { value: 'gas_electric', label: '⚡ Gas & Electric' },
      { value: 'internet_cable', label: '🌐 Internet & Cable' },
      { value: 'phone', label: '📱 Phone' }
    ]
  },
  {
    label: 'Food & Dining',
    children: [
      { value: 'groceries', label: '🍏 Groceries' },
      { value: 'restaurants_bars', label: '🍽️ Restaurants & Bars' },
      { value: 'coffee_shops', label: '🍇 Coffee Shops' }
    ]
  },
  {
    label: 'Travel & Lifestyle',
    children: [
      { value: 'travel_vacation', label: '🧳 Travel & Vacation' },
      { value: 'entertainment_recreation', label: '🎬 Entertainment & Recreation' },
      { value: 'personal', label: '👑 Personal' },
      { value: 'pets', label: '🐶 Pets' },
      { value: 'fun_money', label: '😜 Fun Money' }
    ]
  },
  {
    label: 'Shopping',
    children: [
      { value: 'shopping', label: '🛍️ Shopping' },
      { value: 'clothing', label: '👕 Clothing' },
      { value: 'furniture_housewares', label: '🪑 Furniture & Housewares' },
      { value: 'electronics', label: '📱 Electronics' }
    ]
  },
  {
    label: 'Children',
    children: [
      { value: 'child_care', label: '👶 Child Care' },
      { value: 'child_activities', label: '⚽ Child Activities' }
    ]
  },
  {
    label: 'Education',
    children: [
      { value: 'student_loans', label: '🎓 Student Loans' },
      { value: 'education', label: '🏫 Education' }
    ]
  },
  {
    label: 'Health & Wellness',
    children: [
      { value: 'medical', label: '💊 Medical' },
      { value: 'dentist', label: '🦷 Dentist' },
      { value: 'fitness', label: '💪 Fitness' }
    ]
  },
  {
    label: 'Financial',
    children: [
      { value: 'loan_repayment', label: '🪙 Loan Repayment' },
      { value: 'financial_legal_services', label: '🧾 Financial & Legal Services' },
      { value: 'financial_fees', label: '🏦 Financial Fees' },
      { value: 'cash_atm', label: '🏧 Cash & ATM' },
      { value: 'insurance', label: '☂️ Insurance' },
      { value: 'taxes', label: '🏛️ Taxes' }
    ]
  },
  {
    label: 'Other',
    children: [
      { value: 'uncategorized', label: '❓ Uncategorized' },
      { value: 'check', label: '💵 Check' },
      { value: 'miscellaneous', label: '💲 Miscellaneous' }
    ]
  },
  {
    label: 'Business',
    children: [
      { value: 'advertising_promotion', label: '📣 Advertising & Promotion' },
      {
        value: 'business_utilities_communications',
        label: '📞 Business Utilities & Communications'
      },
      { value: 'employee_wages_compensation', label: '💵 Employee Wages & Compensation' },
      { value: 'business_travel_meals', label: '🍴 Business Travel & Meals' },
      { value: 'business_auto_expenses', label: '🚗 Business Auto Expenses' },
      { value: 'business_insurance', label: '📁 Business Insurance' },
      { value: 'office_supplies_expenses', label: '🖇️ Office Supplies & Expenses' },
      { value: 'office_rent', label: '🏢 Office Rent' },
      { value: 'postage_shipping', label: '📦 Postage & Shipping' }
    ]
  },
  {
    label: 'Transfers',
    children: [
      { value: 'transfer', label: '🔄 Transfer' },
      { value: 'credit_card_payment', label: '💳 Credit Card Payment' },
      { value: 'balance_adjustments', label: '⚖️ Balance Adjustments' }
    ]
  }
];

const FILTER_TABS = [
  { label: 'Categories', value: 'categories' },
  { label: 'Merchants', value: 'merchants' },
  { label: 'Accounts', value: 'accounts' },
  { label: 'Tags', value: 'tags' },
  { label: 'Amount', value: 'amount' },
  { label: 'Other', value: 'other' }
];

export default function FiltersPopover({
  onApply,
  onClear
}: {
  onApply?: (filters: any) => void;
  onClear?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('categories');
  const [categorySearch, setCategorySearch] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  const [selectAll, setSelectAll] = useState(false);

  // Count of selected filters
  const selectedCount = selectedCategories.length;

  // Simple filter logic
  const filteredCategories = CATEGORIES.map((group) => ({
    ...group,
    children: group.children.filter((item) =>
      item.label.toLowerCase().includes(categorySearch.toLowerCase())
    )
  }));

  // Handlers
  const handleToggleCategory = (value: string) => {
    setSelectedCategories((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedCategories([]);
      setSelectAll(false);
    } else {
      const all = CATEGORIES.flatMap((group) => group.children.map((c) => c.value));
      setSelectedCategories(all);
      setSelectAll(true);
    }
  };

  const handleApply = () => {
    onApply?.({ categories: selectedCategories });
    setOpen(false);
  };

  const handleClear = () => {
    setSelectedCategories([]);
    setSelectAll(false);
    setCategorySearch('');
    onClear?.();
    setOpen(false);
  };

  return (
    <div className="relative">
      <button
        className="border px-3 py-1.5 rounded-md flex items-center gap-2 bg-white"
        onClick={() => setOpen((v) => !v)}
        type="button"
      >
        <Filter size={16} />
        Filters
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-2 min-w-[560px] bg-white rounded-lg shadow-xl border">
          <div className="flex h-[380px]">
            {/* Tabs */}
            <div className="w-36 border-r bg-gray-50 p-2 flex flex-col gap-1">
              {FILTER_TABS.map((tab) => (
                <button
                  key={tab.value}
                  className={`text-sm px-3 py-2 rounded text-left font-medium ${
                    activeTab === tab.value
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                  onClick={() => setActiveTab(tab.value)}
                  type="button"
                >
                  {tab.label}
                </button>
              ))}
            </div>
            {/* Filters content */}
            <div className="flex-1 flex flex-col p-3 min-w-[320px]">
              {/* Top: search and summary */}
              <div className="flex items-center mb-2">
                <input
                  className="w-48 border px-2 py-1 rounded text-sm mr-3"
                  placeholder="🔍 Search..."
                  value={categorySearch}
                  onChange={(e) => setCategorySearch(e.target.value)}
                />
                <span className="text-xs text-gray-500 ml-auto">
                  {selectedCount} filter{selectedCount === 1 ? '' : 's'} selected
                </span>
              </div>
              <div className="flex-1 overflow-y-auto">
                {/* Only Categories for now */}
                {activeTab === 'categories' && (
                  <>
                    <div className="mb-2">
                      <label className="flex items-center gap-2 text-sm font-medium">
                        <input type="checkbox" checked={selectAll} onChange={handleSelectAll} />
                        Select all
                      </label>
                    </div>
                    {filteredCategories.map((group) => (
                      <div key={group.label}>
                        <div className="text-xs uppercase font-semibold text-gray-400 py-2">
                          {group.label}
                        </div>
                        {group.children.map((item) => (
                          <label
                            className="flex items-center gap-2 px-2 py-1 cursor-pointer text-sm"
                            key={item.value}
                          >
                            <input
                              type="checkbox"
                              checked={selectedCategories.includes(item.value)}
                              onChange={() => handleToggleCategory(item.value)}
                            />
                            <span>{item.label}</span>
                          </label>
                        ))}
                      </div>
                    ))}
                  </>
                )}
                {/* You can add logic for other tabs here */}
                {activeTab !== 'categories' && (
                  <div className="text-sm text-gray-400 pt-8 text-center">Coming soon</div>
                )}
              </div>
            </div>
          </div>
          {/* Actions */}
          <div className="flex justify-between items-center border-t px-4 py-2 bg-white rounded-b-lg">
            <button
              className="text-gray-500 hover:text-black text-xs font-medium"
              onClick={handleClear}
              type="button"
            >
              Clear
            </button>
            <div>
              <button
                className="text-gray-500 hover:text-black text-xs font-medium mr-2"
                onClick={() => setOpen(false)}
                type="button"
              >
                Cancel
              </button>
              <button
                className={`px-4 py-1.5 rounded font-medium text-white text-xs bg-orange-400 hover:bg-orange-500`}
                onClick={handleApply}
                type="button"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
